import React, { Attributes, ReactElement, useContext } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ROUTE_PARAMS, ROUTE_PATH } from '../../utilities/navigation';
import { AvatarProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChatInbox } from '../../components/chat/ChatInbox';
import { IChatInboxItemProps, ChatInboxItem } from '../../components/chat/ChatInboxItem';
import { IssueStateView } from '../../components/IssueStateView';
import { ChatChannelContext } from '../../context/chat';
import { UserContext } from '../../context/UserInformation';
import { useChatBooking } from '../../hooks/chat/useChatBooking';
import { useChatChannel } from '../../hooks/chat/useChatChannel';
import { useChatListing } from '../../hooks/chat/useChatListing';
import { useChatUser } from '../../hooks/chat/useChatUser';
import { WithID, EUserRole } from '../../types/db';
import { UserChannel, USER_CHANNEL_LAST_UPDATE, CHAT_CHANNEL_LATEST_MESSAGE, USER_CHANNEL_UNSEEN_COUNT, CHAT_CHANNEL_MEMBERS, ChatUser, ChatChannelMember } from '../../types/db/chat';
import { useUserChannels } from '../../hooks/chat/useUserChannels';

/** The Chat inbox page (list of channels) */
export const ChatInboxPage = () => {
    const navigate = useNavigate();
    const { [ROUTE_PARAMS.threadID]: threadID } = useParams();

    const navigateToThread = (threadId: string) => {
        navigate(ROUTE_PATH.inboxBuild(threadId))
    }

    return <ChatInboxContainer
        selectedThreadId={threadID}
        navigateToThread={navigateToThread}
    />
}


interface IChatInboxContainerProps {
    /** The selected thread id (to be able to highlight the current one) */
    selectedThreadId?: string;
    /** Callback to navigate to another thread */
    navigateToThread: (threadId: string) => void;
}
const ChatInboxContainer = (props: IChatInboxContainerProps) => {
    const { t } = useTranslation();
    const userContext = useContext(UserContext);
    if (!userContext) {
        throw new Error("Need UserContext");
    }

    const userId = userContext.user?.firebaseUser?.uid;
    const { channels, isLoading, error } = useUserChannels(userId || '')
    if (!userId) {
        console.warn('UserInboxContainer: No user id');
        return null;
    }

    const renderItem = (channel: (WithID<UserChannel>)) => {
        return <InboxItemContainer key={`${channel.id}-${channel[USER_CHANNEL_LAST_UPDATE]}`}
            userId={userId} userChannel={channel} {...props} />
    }

    if (isLoading) {
        return <ChatInbox loading />
    }

    if (!!error) {
        return <IssueStateView
            title={t('chat.inbox.errorTitle')}
            description={t('chat.inbox.errorContent')}
        />
    }

    if (!channels.length) {
        return <IssueStateView
            title={t('user.inbox.emptyTitle')}
            description={t('user.inbox.emptyContent')}
        // button={{
        //     text: 'Join the community chat!!:p',
        //     onActionClick: () => createChannel(userId)
        // }}
        />
    }

    return (
        <ChatInbox
            items={channels}
            renderItem={renderItem}
        />
    );
}

interface IInboxItemContainerProps extends IChatInboxContainerProps {
    /** The current user id */
    userId: string;
    /** The current user channel */
    userChannel: WithID<UserChannel>;
}
/** Takes care of each item in the inbox */
const InboxItemContainer = ({ userId, userChannel, navigateToThread, selectedThreadId }: IInboxItemContainerProps) => {
    const { channel, error, isLoading } = useChatChannel(userChannel.id);

    if (isLoading) {
        return <ChatInbox loading />
    }

    if (!channel || error) {
        return <ChatInbox error />
    }

    const generalProps: IChatInboxItemProps & Attributes = {
        key: channel.id + channel[CHAT_CHANNEL_LATEST_MESSAGE]?.timestamp,
        title: channel.title,
        image: { src: channel.image },
        isSelected: channel.id === selectedThreadId,
        description: userChannel.draft?.message || channel[CHAT_CHANNEL_LATEST_MESSAGE]?.message || "",
        timestamp: !!channel[CHAT_CHANNEL_LATEST_MESSAGE]?.timestamp ? new Date(+channel[CHAT_CHANNEL_LATEST_MESSAGE].timestamp) : undefined,
        unreadCount: userChannel[USER_CHANNEL_UNSEEN_COUNT] || 0,
        isDraft: !!userChannel.draft?.message,
        isOwnLastMessage: channel[CHAT_CHANNEL_LATEST_MESSAGE]?.sender === userId,
        onClick: () => navigateToThread(channel.id)
    }

    if (channel.type === 'listing' && !!channel.listing) {
        if (!!channel.booking) {
            //if it has a booking attached to it
            return <InboxItemListingBookingChatContainer channelId={channel.id} listingId={channel.listing} bookingId={channel.booking}>
                {
                    ({ listing, booking, host, cohost, guest, isLoading }) => {
                        if (isLoading) return <ChatInboxItem loading />
                        const listingProps: Pick<IChatInboxItemProps, 'title' | 'image'> = {
                            title: channel.title || listing?.name || '',
                            image: { src: channel.image || listing?.image || "" },
                        }
                        const bookingProps: Pick<IChatInboxItemProps, 'status' | 'subtitle'> = {
                            status: booking?.status,
                            subtitle: booking?.dates.map(date => new Date(+date).toString()).join(',')
                        }
                        return <ChatInboxItem
                            {...{
                                ...generalProps,
                                ...listingProps,
                                ...bookingProps,
                                avatars: getAvatarListingBooking(userId, { host, cohost, guest }),
                            }} />
                    }
                }
            </InboxItemListingBookingChatContainer>
        } else {
            return <InboxItemListingGroupChatContainer listingId={channel.listing}>
                {({ listing, isLoading }) => {
                    if (isLoading) {
                        return <ChatInboxItem loading />
                    }
                    const listingProps: Pick<IChatInboxItemProps, 'title' | 'image'> = {
                        title: channel.title || listing?.name || '',
                        image: { src: channel.image || listing?.image || "" },
                    }
                    return <ChatInboxItem
                        {...{
                            ...generalProps,
                            ...listingProps,
                        }} />
                }}
            </InboxItemListingGroupChatContainer>
        }
    }
    if (channel.type === 'event' && !!channel.event) {
        //TODO Event item
    }

    if (channel.type === "direct") {
        //for 1-1 direct message display interlocutor info
        const interUser = Object.keys(channel[CHAT_CHANNEL_MEMBERS]).filter(key => key !== userId);
        const interUserId = interUser[0];

        if (!interUserId) {
            console.error("User not found")
            return <ChatInboxItem {...generalProps} />
        }

        return <InboxItemDirectChatContainer userId={interUserId}>
            {
                ({ user }) => <ChatInboxItem
                    {...{
                        ...generalProps,
                        title: user?.displayName || '',
                        image: { src: user?.avatar || "" },
                    }} />
            }
        </InboxItemDirectChatContainer>
    }

    return <ChatInboxItem
        {...generalProps} />
}

interface IInboxItemDirectChatContainerProps {
    userId: string;
    children: (data: ReturnType<typeof useChatUser>) => ReactElement;
}
const InboxItemDirectChatContainer = ({ userId, children }: IInboxItemDirectChatContainerProps) => {
    const args = useChatUser(userId);
    return children(args);
}

interface IInboxItemListingGroupChatContainerProps {
    listingId: string;
    children: (data: ReturnType<typeof useChatListing>) => ReactElement;
}
const InboxItemListingGroupChatContainer = ({ listingId, children }: IInboxItemListingGroupChatContainerProps) => {
    const args = useChatListing(listingId)
    return children(args);
}

interface IInboxItemListingBookingChatContainerProps {
    channelId: string;
    listingId: string;
    bookingId: string;
    children: (data: ReturnType<typeof useChatListing>
        & ReturnType<typeof useChatBooking>
        & { host?: WithID<ChatUser> }
        & { cohost?: WithID<ChatUser> }
        & { guest?: WithID<ChatUser> }
    ) => ReactElement;
}
const InboxItemListingBookingChatContainer = ({ channelId, listingId, bookingId, children }: IInboxItemListingBookingChatContainerProps) => {
    const chatChannelContext = useContext(ChatChannelContext);
    if (!chatChannelContext) {
        throw new Error('InboxItemListingBookingChatContainer: ChatChannelContext needed')
    }
    const { members } = chatChannelContext;
    const args1 = useChatListing(listingId);
    const args2 = useChatBooking(bookingId);

    return children({
        listing: args1.listing,
        booking: args2.booking,
        host: exctractMemberByRole(members, EUserRole.HOST),
        cohost: exctractMemberByRole(members, EUserRole.COHOST),
        guest: exctractMemberByRole(members, EUserRole.GUEST),
        isLoading: args1.isLoading || args2.isLoading,
        error: !!args1.error || !!args2.error ? [args1.error, args2.error].join(' ') : undefined,
    })
}

function exctractMemberByRole<T extends ChatChannelMember>(members: { [id: string]: T }, role: EUserRole): WithID<T> | undefined {
    const memberKey = Object.keys(members).find(memberId => members[memberId].role === role);
    return !!memberKey ? { id: memberKey, ...members[memberKey] } : undefined;
}

const getAvatarListingBooking = (userId: string,
    members: { host?: WithID<ChatUser> }
        & { cohost?: WithID<ChatUser> }
        & { guest?: WithID<ChatUser> }): AvatarProps | [AvatarProps, AvatarProps] | undefined => {
    const { host, cohost, guest } = members;
    let avatars: { src: string }[] = [];
    if (guest?.id === userId) {
        //if current user is guest: show host and cohost
        avatars = [host, cohost].filter(val => !!val).map(val => ({ src: val.avatar }))
    } else if (host?.id === userId) {
        //if the current user is host: show guest and cohost
        avatars = [guest, cohost].filter(val => !!val).map(val => ({ src: val.avatar }))
    } else if (cohost?.id === userId) {
        //if the current user is cohost: show guest
        avatars = [guest].filter(val => !!val).map(val => ({ src: val.avatar }))
    } else {
        //if none of the above/no role: return no avatars
        return undefined;
    }
    //format avatar depending on length
    switch (avatars.length) {
        case 0: return undefined;
        case 1: return avatars[0];
        case 2:
        default: return [avatars[0], avatars[1]];
    }
}